import { useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiRoutes } from "config/api/apiRoutes";
import { LIST_DEFAULT_STALE_TIME } from "utils/setting";
import { ITemplateCreateData, ITemplateUpdateData, TemplateListFilter } from "models/whatsAppTemplateModel";
import { useWhatsAppTemplateApi } from "./whatsAppTemplateApi";


export const useGetPaginatedTemplates = (pageSize: number, enabled?: boolean, filter?: TemplateListFilter) => {
    const { getWhatsAppPaginatedTemplateList } = useWhatsAppTemplateApi();

    const query = useInfiniteQuery([apiRoutes.getWhatsAppTemplates, filter],
        ({ pageParam = 1 }) => getWhatsAppPaginatedTemplateList(pageParam, pageSize, filter), {
        getNextPageParam: (lastPage, allPages) => {
            const nextPage = lastPage.items.length === pageSize ? allPages.length + 1 : undefined;
            return nextPage;
        },
        enabled: enabled,
        staleTime: LIST_DEFAULT_STALE_TIME,
    });

    return query;
}

export const useGetTemplateLookups = (isStaticContent: boolean) => {
    const { getWhatsAppTemplateLookups } = useWhatsAppTemplateApi();

    const query = useQuery([apiRoutes.getWhatsAppTemplateLookups], () => getWhatsAppTemplateLookups(isStaticContent), {
    })

    return query;
}

export const useGetTemplateById = (id: number) => {
    const { getWhatsAppTemplate } = useWhatsAppTemplateApi();

    const query = useQuery([apiRoutes.getWhatsAppTemplateById, id], () => getWhatsAppTemplate(id), {
        enabled: id > 0,
        cacheTime: 0
    })
    return query;
}

export const useGetTemplateLanguages = () => {
    const { getTemplateLaguageList } = useWhatsAppTemplateApi();

    const query = useQuery([apiRoutes.getTemplateLanguages], () => getTemplateLaguageList(), {
        staleTime: Infinity
    });

    return query
}

export const useGetTemplateCategories = () => {
    const { getTemplateCategoryList } = useWhatsAppTemplateApi();

    const query = useQuery([apiRoutes.getTemplateCategories], () => getTemplateCategoryList(), {
        staleTime: Infinity
    });

    return query
}

export const useGetTemplateStatuses = () => {
    const { getTemplateStatusList } = useWhatsAppTemplateApi();

    const query = useQuery([apiRoutes.getTemplateStatuses], () => getTemplateStatusList(), {
        staleTime: Infinity
    });

    return query
}

export const useSyncTemplates = () => {
    const { syncTemplates } = useWhatsAppTemplateApi();

    const [isLoading, setLoading] = useState<boolean>(false)

    const queryClient = useQueryClient();

    const synchronizeTemplates = async () => {
        setLoading(true)
        return syncTemplates()
            .then(res => {
                queryClient.invalidateQueries({ queryKey: [apiRoutes.getWhatsAppTemplates] })
                return true
            })
            .catch((error) => false)
            .finally(() => setLoading(false))
    }

    return { synchronizeTemplates, isLoading }
}

export const useAddTemplate = () => {
    const { createTemplate } = useWhatsAppTemplateApi();

    const queryClient = useQueryClient();

    const mutation = useMutation((data: ITemplateCreateData) => createTemplate(data), {
        onSuccess: () => {
            queryClient.invalidateQueries([apiRoutes.getWhatsAppTemplates]) // invalidates template list
            queryClient.invalidateQueries([apiRoutes.getWAUtilityAndMarketingTemplateLookups]) // invalidates utility and marketing template list
            queryClient.invalidateQueries([apiRoutes.getWAMediaAndUtilityAndMarketingTemplateLookups]) // invalidates media template list
        },
    })

    return mutation;
}

export const useEditTemplate = () => {
    const { updateTemplate } = useWhatsAppTemplateApi();

    const queryClient = useQueryClient();

    const mutation = useMutation((data: ITemplateUpdateData) => updateTemplate(data), {
        onSuccess: () => {
            queryClient.invalidateQueries([apiRoutes.getWhatsAppTemplates]) // invalidates template list
            queryClient.invalidateQueries([apiRoutes.getWAUtilityAndMarketingTemplateLookups]) // invalidates utility and marketing template list
            queryClient.invalidateQueries([apiRoutes.getWAMediaAndUtilityAndMarketingTemplateLookups]) // invalidates media template list
        },
    })

    return mutation;
}

export const useDeleteTemplate = () => {
    const { deleteTemplate } = useWhatsAppTemplateApi();

    const queryClient = useQueryClient();

    const mutation = useMutation((templateId: number) => deleteTemplate(templateId), {
        onSuccess: () => {
            queryClient.invalidateQueries([apiRoutes.getWhatsAppTemplates]) // invalidates template list
            queryClient.invalidateQueries([apiRoutes.getWAUtilityAndMarketingTemplateLookups]) // invalidates utility and marketing template list
            queryClient.invalidateQueries([apiRoutes.getWAMediaAndUtilityAndMarketingTemplateLookups]) // invalidates media template list
        },
    })

    return mutation;
}

export const useUploadTemplateMediaFile = () => {
    const { uploadTemplateMediaFile } = useWhatsAppTemplateApi();

    const [isLoading, setLoading] = useState<boolean>(false)

    const getHeaderHandle = async (fileBase64: string, fileName: string, fileMimeType: string) => {
        setLoading(true)
        return uploadTemplateMediaFile(fileBase64, fileName, fileMimeType)
            .then(res => {
                return res
            })
            .finally(() => setLoading(false))
    }

    return { getHeaderHandle, isLoading }
}

export const useGetUtilityAndMarketingTemplateLookups = (isStaticContent: boolean, hasButtons?: boolean) => {
    const { getWAUtilityAndMarketingTemplateLookups } = useWhatsAppTemplateApi();

    const query = useQuery([apiRoutes.getWAUtilityAndMarketingTemplateLookups], () => getWAUtilityAndMarketingTemplateLookups(isStaticContent, hasButtons), {
    })

    return query;
}

export const useGetMediaAndUtilityAndMarketingTemplateLookups = (isStaticContent: boolean, hasButtons?: boolean) => {
    const { getWAMediaAndUtilityAndMarketingTemplateLookups } = useWhatsAppTemplateApi();

    const query = useQuery([apiRoutes.getWAMediaAndUtilityAndMarketingTemplateLookups], () => getWAMediaAndUtilityAndMarketingTemplateLookups(isStaticContent, hasButtons), {
    })

    return query;
}

export const useGetIsTemplateUsed = (id: number) => {
    const { getIsTemplateUsed } = useWhatsAppTemplateApi();

    const query = useQuery([apiRoutes.getIsTemplateUsed, id], () => getIsTemplateUsed(id), {
      cacheTime: 0,
      enabled: id > 0
    });

    return query
}

export const useGetNameWhereTemplateUsed = (id: number) => {
    const { getNameWhereTemplateUsed } = useWhatsAppTemplateApi();

    const query = useQuery([apiRoutes.getNameWhereTemplateUsed, id], () => getNameWhereTemplateUsed(id), {
      cacheTime: 0,
      enabled: id > 0
    });

    return query
}